import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NEVER, Observable } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, tap } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { Company } from '../../models/models';
import { DocumentService } from '../../services/document.service';
import { FolderService } from '../../services/folder.service';
import { StringHelperService } from '../../../../services/string-helper.service';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';

@Component({
  selector: 'app-new-folder',
  templateUrl: './new-folder.component.html',
  styleUrls: ['./new-folder.component.scss'],
  imports: [
    AsyncPipe,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    MatSelect,
    MatLabel,
    MatOption,
    MatButton,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions
  ],
  standalone: true
})
export class NewFolderComponent implements OnInit {
  formGroup = new FormGroup({
    name: new FormControl('', Validators.required),
    companyId: new FormControl('', Validators.required)
  });
  companies$: Observable<Company[]> | null = null;

  constructor(
    private documentService: DocumentService,
    private folderService: FolderService,
    private snackBar: MatSnackBar,
    private stringHelperService: StringHelperService,
    private dialogRef: MatDialogRef<NewFolderComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { folderId: number | null; companyId: string | null }
  ) {}

  ngOnInit() {
    this.companies$ = this.documentService.getCompanies();
    if (this.data.companyId) {
      const customerControl = this.formGroup.get('companyId');
      customerControl?.setValue(this.data.companyId);
      customerControl?.disable();
    }
  }

  create() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.snackBar.open('Please select all required fields', 'x');
      return;
    }

    if (this.stringHelperService.notAllowedSpecialChars(this.formGroup.get('name')?.value || '')) {
      this.snackBar.open('Folder name cannot contain some special characters. Please rename the folder.', 'x');
      return;
    }
    this.createFolder();
  }

  clear() {
    this.formGroup.reset();
  }

  private createFolder() {
    const parentFolderId = this.data.folderId ? this.data.folderId.toString() : null;
    this.folderService
      .create({ ...this.formGroup.getRawValue(), parentFolderId })
      .pipe(
        tap(folder => {
          this.dialogRef.close(folder);
          this.snackBar.open('The folder was created successfully', 'x', { panelClass: 'snackbar-green' });
        }),
        catchError(error => {
          if (error.status === 409) {
            this.snackBar.open('This folder already exists', 'x');
          } else {
            this.snackBar.open('There was an error. Please try again.', 'x');
          }
          console.error(error);
          return NEVER;
        })
      )
      .subscribe();
  }
}
